class KillaPabloPopup {
  constructor(root) {
    this.root = root;
    this.cookieName = 'killa-pablo-deals-popup';
    this.signupCookieName = root.dataset.signupCookieName || 'killa-pablo-deals-signup';
    this.signupCookieValue = root.dataset.signupCookieValue || 'true';
    this.listId = root.dataset.listId;
    this.customSource = root.dataset.customSource;
    this.dismissDuration = parseInt(root.dataset.cookieDays, 10) * 24 * 60 * 60 * 1000;
    this.subscribedDuration = 365 * 24 * 60 * 60 * 1000;
    this.isLoggedIn = root.dataset.loggedIn === 'true';

    this.form = root.querySelector('#killa_pablo_signup');
    this.emailInput = root.querySelector('#killaPabloEmail');
    this.phoneInput = root.querySelector('#killaPabloPhone');
    this.consentCheckbox = root.querySelector('#killaPabloConsent');
    this.submitBtn = root.querySelector('.killa-pablo-popup--submit');
    this.buttonContainer = root.querySelector('.popup--button-container');
    this.validationMsg = root.querySelector('.popup--validation');
    this.errorMsg = root.querySelector('.popup--error');
    this.successMsg = root.querySelector('.popup--success');
    this.content = root.querySelector('.popup-form--content');

    // Visit the page with ?kp_popup=reset to clear both cookies and
    // re-trigger the popup + purchase lock (for testing).
    if (new URLSearchParams(window.location.search).get('kp_popup') === 'reset') {
      this.clearCookie(this.cookieName);
      this.clearCookie(this.signupCookieName);
      document.body.classList.add('kp-signup-locked');
    }

    this.bindEvents();
    this.bindPurchaseGate();
    this.scheduleShow();

    // Diagnostic breadcrumb — explains at a glance why the popup did or
    // didn't appear. Visible in the browser console (F12).
    console.log(
      '[Killa Pablo popup] logged in:', this.isLoggedIn,
      '| signed up:', this.getCookie(this.signupCookieName) === this.signupCookieValue,
      '| will show:', this.isLoggedIn && this.getCookie(this.signupCookieName) !== this.signupCookieValue
    );
  }

  bindEvents() {
    this.submitBtn.classList.toggle('is-disabled', !this.consentCheckbox.checked);
    this.consentCheckbox.addEventListener('change', () => {
      this.submitBtn.classList.toggle('is-disabled', !this.consentCheckbox.checked);
    });

    // Phone field only accepts digits and a leading + (covers typing,
    // pasting and autofill).
    this.phoneInput.addEventListener('input', () => {
      const cleaned = this.phoneInput.value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
      if (this.phoneInput.value !== cleaned) this.phoneInput.value = cleaned;
    });

    this.root.querySelector('.popup-form--close-button').addEventListener('click', (e) => {
      e.preventDefault();
      this.setCookie(this.dismissDuration);
      this.hide();
    });

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
  }

  // Add to Cart drawer toggles stay locked until the visitor has BOTH
  // submitted the signup form and logged into their account. Capture
  // phase fires before drawer.js's element-level listeners, so
  // stopPropagation here keeps the product drawer from opening.
  bindPurchaseGate() {
    document.addEventListener(
      'click',
      (e) => {
        if (!document.body.classList.contains('kp-signup-locked')) return;

        const toggle = e.target.closest('.button--blue-fill.drawer-toggle[data-toggle="productDrawer"]');
        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();

        if (!this.isLoggedIn) {
          // Logged out — send them to account login and bring them
          // back to this page afterwards (the popup then appears).
          window.location.href = `/account/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        } else {
          // Logged in but locked means they haven't signed up yet.
          this.show();
        }
      },
      true
    );
  }

  scheduleShow() {
    // The popup is for logged-in customers only — logged-out visitors
    // are routed to account login by the purchase gate instead.
    if (!this.isLoggedIn) return;

    // Already signed up — nothing left to collect.
    if (this.getCookie(this.signupCookieName) === this.signupCookieValue) return;

    // Appears immediately on landing, every visit, until they sign up —
    // including straight after they return from /account/login.
    this.show();
  }

  show() {
    this.root.classList.remove('disabled');
    document.body.classList.add('verifier', 'no-scroll');
  }

  hide() {
    this.root.classList.add('disabled');
    document.body.classList.remove('verifier', 'no-scroll');
  }

  submit() {
    const email = this.emailInput.value.trim();
    const phone = this.normalisePhone(this.phoneInput.value);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.showValidation('Please use a valid email address.');
      return;
    }

    if (!phone) {
      this.showValidation('Please use a valid phone number, e.g. 07123 456789.');
      return;
    }

    this.validationMsg.classList.add('is-hidden');
    this.errorMsg.classList.add('is-hidden');
    this.buttonContainer.classList.add('active');

    const options = {
      method: 'POST',
      headers: {
        revision: '2024-10-15',
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.customSource,
            // The client subscriptions endpoint infers consent from the
            // identifiers sent (email → email marketing, phone → SMS);
            // an explicit `subscriptions` block is server-API-only and
            // makes this endpoint reject the whole request with a 400.
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  phone_number: phone
                }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: this.listId } }
          }
        }
      })
    };

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=XYTG6S', options)
      .then((response) => {
        if (response.status === 202) {
          setTimeout(() => {
            this.successMsg.classList.remove('is-hidden');
            this.content.classList.add('success');
            this.setCookie(this.subscribedDuration);
            this.setSignupCookie();
            document.body.classList.remove('kp-signup-locked');

            setTimeout(() => this.hide(), 4000);
          }, 800);
        } else {
          setTimeout(() => {
            this.errorMsg.classList.remove('is-hidden');
            this.buttonContainer.classList.remove('active');
          }, 500);
        }
      })
      .catch((err) => {
        console.error('Error submitting Killa Pablo deals form:', err);
        this.errorMsg.classList.remove('is-hidden');
        this.buttonContainer.classList.remove('active');
      });
  }

  showValidation(message) {
    this.validationMsg.textContent = message;
    this.validationMsg.classList.remove('is-hidden');
    this.buttonContainer.classList.remove('active');
  }

  // Klaviyo requires E.164 — accepts UK mobiles as 07..., 447... or +447...
  normalisePhone(raw) {
    let value = raw.replace(/[\s\-().]/g, '');

    if (/^07\d{9}$/.test(value)) value = '+44' + value.slice(1);
    else if (/^447\d{9}$/.test(value)) value = '+' + value;
    else if (/^00447\d{9}$/.test(value)) value = '+' + value.slice(2);

    return /^\+[1-9]\d{7,14}$/.test(value) ? value : null;
  }

  setCookie(duration) {
    const d = new Date();
    d.setTime(d.getTime() + duration);
    document.cookie = `${this.cookieName}=true;expires=${d.toUTCString()};path=/`;
  }

  setSignupCookie() {
    const d = new Date();
    d.setTime(d.getTime() + this.subscribedDuration);
    document.cookie = `${this.signupCookieName}=${this.signupCookieValue};expires=${d.toUTCString()};path=/`;
  }

  clearCookie(name) {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }

  getCookie(name = this.cookieName) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }
}

const killaPabloPopupRoot = document.getElementById('killaPabloPopup');
if (killaPabloPopupRoot) new KillaPabloPopup(killaPabloPopupRoot);
